import { FormOutlined } from "@ant-design/icons"
import {
  Button,
  Modal,
  Form,
  DatePicker,
  Input,
  InputNumber,
  Select,
  Space,
  Radio,
  message,
} from "antd"
import React, { useState, useEffect } from "react"
import dayjs from "dayjs"
import { doc, getDoc, setDoc, runTransaction } from "firebase/firestore"
import { db, storage } from "../../firebase/config"
import { ref, uploadBytes, getDownloadURL } from "firebase/storage"
import { pdf } from "@react-pdf/renderer"
import ReprintPDFDocument from "./ReprintPDFDocument"

const { TextArea } = Input

const TRANSLATIONS = {
  en: {
    title: "Reprint Form",
    date: "Date",
    reprintReason: "Reprint Reason",
    actNumber: "Act No.",
    datedWith: "Dated",
    productName: "Product Name",
    reprintQuantity: "Reprint Quantity",
    copies: "copies",
    orderNumber: "Order Number",
    complaintReason: "Complaint Reason",
    reasons: {
      production: "Production defect",
      layout: "Layout error",
      clientFault: "Defect due to client fault (by agreement)",
      other: "Other",
    },
    otherSpecify: "Specify other reason",
    deadline: "Deadline",
    submit: "Generate Form",
    cancel: "Cancel",
    reprinting: "Reprinting",
  },
  lv: {
    title: "Atkārtotas drukas forma",
    date: "Datums",
    reprintReason: "Atkārtotas drukas iemesls",
    actNumber: "Akta Nr.",
    datedWith: "datēts ar",
    productName: "Produkta nosaukums",
    reprintQuantity: "Atkārtotas drukas daudzums",
    copies: "eksemplāri",
    orderNumber: "Pasūtījuma numurs",
    complaintReason: "Atkārtotas drukas (Complaint) iemesls",
    reasons: {
      production: "Ražošanas defekts",
      layout: "Izkārtojuma kļūda",
      clientFault: "Defekts klienta vainas dēļ (pēc vienošanās)",
      other: "Cits",
    },
    otherSpecify: "Norādiet citu iemeslu",
    deadline: "Izpildes termiņš",
    submit: "Ģenerēt formu",
    cancel: "Atcelt",
    reprinting: "Reprinting",
  },
}

export default function ComplaintReprintForm({ complaint }) {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [form] = Form.useForm()
  const [language, setLanguage] = useState("lv")
  const [actNumber, setActNumber] = useState(null)
  const [selectedReason, setSelectedReason] = useState("production")
  const [loading, setLoading] = useState(false)
  const [isReprinting, setIsReprinting] = useState(false)

  const t = TRANSLATIONS[language]

  const isApproved = complaint?.status === "Approved"

  // Check if complaint already has a reprint request
  useEffect(() => {
    if (complaint?.reprintRequestId) {
      setIsReprinting(true)
    }
  }, [complaint])

  useEffect(() => {
    const fetchActNumber = async () => {
      try {
        const counterRef = doc(db, "counters", "reprintActNumber")
        const counterSnap = await getDoc(counterRef)

        if (counterSnap.exists()) {
          setActNumber(counterSnap.data().current + 1)
        } else {
          await setDoc(counterRef, { current: 1000 })
          setActNumber(1001)
        }
      } catch (error) {
        console.error("Error fetching act number:", error)
        message.error("Failed to load act number")
        setActNumber(1001)
      }
    }

    if (isModalOpen) {
      fetchActNumber()
    }
  }, [isModalOpen])

  const showModal = (e) => {
    e.stopPropagation()

    if (!isApproved) {
      message.warning("Reprint can be created only after the complaint is approved.")
      return
    }

    const orderSnapshot = complaint?.orderSnapshot || {}
    const complaintDate = complaint?.createdAt?.seconds
      ? dayjs.unix(complaint.createdAt.seconds)
      : dayjs()

    form.setFieldsValue({
      date: complaintDate,
      actDate: dayjs(),
      productName: orderSnapshot?.selectedProduct?.product?.productName || "",
      orderNumber: complaint?.orderNumber || complaint?.orderId || "",
      reprintQuantity: orderSnapshot?.amountCreated || 0,
      complaintReason: "production",
      deadline: dayjs().add(2, "weeks"),
    })

    setIsModalOpen(true)
  }

  const handleOk = async () => {
    setLoading(true)
    try {
      const values = await form.validateFields()

      // 1. Increment act number atomically
      const counterRef = doc(db, "counters", "reprintActNumber")
      const newActNumber = await runTransaction(db, async (transaction) => {
        const counterDoc = await transaction.get(counterRef)

        if (!counterDoc.exists()) {
          transaction.set(counterRef, { current: 1001 })
          return 1001
        }

        const currentNumber = counterDoc.data().current
        const nextNumber = currentNumber + 1

        transaction.update(counterRef, { current: nextNumber })
        return nextNumber
      })

      // 2. Prepare reprint data
      const reprintData = {
        actNumber: newActNumber,
        date: values.date.toDate(),
        actDate: values.actDate.toDate(),
        reprintReason: values.reprintReason || "",
        productName: values.productName,
        reprintQuantity: values.reprintQuantity,
        orderNumber: values.orderNumber,
        complaintReason: values.complaintReason,
        otherReasonText: values.otherReasonText || "",
        deadline: values.deadline.toDate(),
        complaintId: complaint?.id || null,
        createdAt: new Date(),
        language: language,
      }

      // 3. Generate PDF
      const pdfDoc = <ReprintPDFDocument data={reprintData} language={language} />
      const blob = await pdf(pdfDoc).toBlob()

      // 4. Upload PDF to Firebase Storage
      const storageRef = ref(storage, `reprintForms/ACT-${newActNumber}.pdf`)
      await uploadBytes(storageRef, blob)
      const pdfUrl = await getDownloadURL(storageRef)

      // 5. Save reprint request to Firestore
      const reprintRequestRef = doc(db, "reprintRequests", `ACT-${newActNumber}`)
      await setDoc(reprintRequestRef, {
        ...reprintData,
        pdfUrl,
        pdfPath: `reprintForms/ACT-${newActNumber}.pdf`,
        orderSnapshot: complaint?.orderSnapshot || null,
        complaintInfo: {
          id: complaint?.id,
          type: complaint?.type,
          responsibility: complaint?.responsibility,
          comment: complaint?.comment,
        },
        status: "pending",
      })

      // 6. Update complaint with reprint request reference
      if (complaint?.id) {
        const complaintRef = doc(db, "complaints", complaint.id)
        await setDoc(complaintRef, { reprintRequestId: reprintRequestRef.id }, { merge: true })
      }

      message.success(`Reprint form created with Act No. ${newActNumber}`)
      setIsReprinting(true)
      setIsModalOpen(false)
      form.resetFields()
    } catch (error) {
      console.error("Error creating reprint form:", error)
      message.error("Failed to create reprint form")
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    setIsModalOpen(false)
    form.resetFields()
  }

  return (
    <div>
      <Button
        size="small"
        icon={<FormOutlined />}
        onClick={showModal}
        disabled={isReprinting || !isApproved}>
        {isReprinting ? "Reprinting" : "Reprint"}
      </Button>
      <Modal
        title={
          <Space style={{ width: "100%", justifyContent: "space-between" }}>
            <Space direction="vertical">
              <span>{t.title}</span>
              <Radio.Group
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                size="small">
                <Radio.Button value="lv">LV</Radio.Button>
                <Radio.Button value="en">EN</Radio.Button>
              </Radio.Group>
            </Space>
          </Space>
        }
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        width={700}
        okText={t.submit}
        cancelText={t.cancel}
        confirmLoading={loading}>
        <Form form={form} layout="vertical" style={{ marginTop: 20, width: "100%" }}>
          <Form.Item
            label={t.date}
            name="date"
            rules={[{ required: true, message: "Please select date" }]}>
            <DatePicker style={{ width: "100%" }} format="DD.MM.YYYY" />
          </Form.Item>

          <Form.Item label={t.reprintReason} name="reprintReason">
            <TextArea rows={2} placeholder={t.reprintReason} />
          </Form.Item>

          <Space style={{ width: "100%", gap: 16 }}>
            <Form.Item label={t.actNumber} style={{ flex: 1, marginBottom: 0 }}>
              <Input value={actNumber} disabled addonBefore="#" placeholder="Loading..." />
            </Form.Item>

            <Form.Item
              label={t.datedWith}
              name="actDate"
              style={{ flex: 1, marginBottom: 0 }}
              rules={[{ required: true, message: "Please select date" }]}>
              <DatePicker style={{ width: "100%" }} format="DD.MM.YYYY" />
            </Form.Item>
          </Space>

          <Form.Item
            label={t.productName}
            name="productName"
            style={{ marginTop: 16 }}
            rules={[{ required: true, message: "Please enter product name" }]}>
            <Input placeholder={t.productName} />
          </Form.Item>

          <Space style={{ width: "100%", gap: 16 }}>
            <Form.Item
              label={`${t.reprintQuantity} (${t.copies})`}
              name="reprintQuantity"
              style={{ flex: 1, marginBottom: 0 }}
              rules={[{ required: true, message: "Please enter quantity" }]}>
              <InputNumber style={{ width: "100%" }} min={1} placeholder="0" />
            </Form.Item>

            <Form.Item
              label={t.orderNumber}
              name="orderNumber"
              style={{ flex: 1, marginBottom: 0 }}
              rules={[{ required: true, message: "Please enter order number" }]}>
              <Input placeholder={t.orderNumber} />
            </Form.Item>
          </Space>

          <Form.Item
            label={t.complaintReason}
            name="complaintReason"
            style={{ marginTop: 16 }}
            rules={[{ required: true, message: "Please select reason" }]}>
            <Select onChange={setSelectedReason} placeholder={t.complaintReason}>
              <Select.Option value="production">{t.reasons.production}</Select.Option>
              <Select.Option value="layout">{t.reasons.layout}</Select.Option>
              <Select.Option value="clientFault">{t.reasons.clientFault}</Select.Option>
              <Select.Option value="other">{t.reasons.other}</Select.Option>
            </Select>
          </Form.Item>

          {selectedReason === "other" && (
            <Form.Item
              label={t.otherSpecify}
              name="otherReasonText"
              rules={[{ required: true, message: "Please specify other reason" }]}>
              <Input placeholder={t.otherSpecify} />
            </Form.Item>
          )}

          <Form.Item
            label={t.deadline}
            name="deadline"
            rules={[{ required: true, message: "Please select deadline" }]}>
            <DatePicker style={{ width: "100%" }} format="DD.MM.YYYY" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
